import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { api, ApiError, setSessionExpiredHandler, tokenStore, unwrap } from "@/lib/api";

import { AuthContext, ME_QUERY_KEY, type AuthContextValue } from "./authContext";

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  /**
   * access token 只在記憶體，所以重新整理後一定是 null。
   * 但 refresh token 在 localStorage，api-client 會在第一個 401 時自動換發，
   * 所以這裡只要判斷「有沒有 refresh token」就知道值不值得嘗試取得 /me/。
   */
  const [hasSession, setHasSession] = useState(() => Boolean(tokenStore.getRefresh()));

  const meQuery = useQuery({
    queryKey: ME_QUERY_KEY,
    enabled: hasSession,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => unwrap(await api.GET("/me/")),
  });

  const logout = useCallback(async () => {
    const refresh = await tokenStore.getRefresh();
    if (refresh) {
      // 讓後端把 refresh token 加入黑名單。失敗也要繼續清本地狀態
      await api.POST("/auth/logout/", { body: { refresh } }).catch(() => undefined);
    }
    await tokenStore.clear();
    setHasSession(false);
    queryClient.clear();
  }, [queryClient]);

  const login = useCallback(
    async (account: string, password: string) => {
      // 這裡刻意解構後再判斷：openapi-fetch 的回傳是 discriminated union，
      // 用 `result.error || !result.data` 這種混合條件會讓 TS 的窄化失效。
      const { data, error, response } = await api.POST("/auth/login/", {
        // 後端依內容判斷是 email 還是手機號碼，前端不分流
        body: { username: account, password },
      });
      if (!data) {
        // simplejwt 的 401 與登入節流的 429 都帶英文的 detail，而 ApiError 有
        // detail 就不用 fallback —— 原本把中文訊息當 fallback 傳進去，畫面上
        // 實際顯示的一直是英文。所以這兩種狀況直接不給 body。
        if (response.status === 401) {
          throw new ApiError(undefined, 401, "帳號或密碼不正確。");
        }
        if (response.status === 429) {
          throw new ApiError(undefined, 429, "登入嘗試次數過多，請稍候一分鐘再試。");
        }
        throw new ApiError(error, response.status, "登入失敗。");
      }
      await tokenStore.setTokens({ access: data.access, refresh: data.refresh });
      setHasSession(true);
      // 登入回傳已經帶了完整的 user，直接寫進快取，省掉一次 /me/
      queryClient.setQueryData(ME_QUERY_KEY, data.user);
    },
    [queryClient],
  );

  // refresh 也失敗時（refresh token 過期或已被列入黑名單）清掉 session
  useEffect(() => {
    setSessionExpiredHandler(() => {
      void tokenStore.clear();
      setHasSession(false);
      queryClient.clear();
    });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      me: hasSession ? (meQuery.data ?? null) : null,
      isLoading: hasSession && meQuery.isLoading,
      login,
      logout,
    }),
    [hasSession, meQuery.data, meQuery.isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
